"use client";

import * as React from "react";
import { useRouter } from "@tanstack/react-router";
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconPlus,
} from "@tabler/icons-react";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { z } from "zod";

import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AddVenueDialog, EditVenueDialog } from "@/features/venue/components";
import { useUpdateVenue } from "@/hooks/use-venues";
import { formatPhoneNumber, cn } from "@/lib/utils";
import { DeleteVenueDialog } from "@/components/delete-venue-dialog";

// Venue schema
export const venueSchema = z.object({
  id: z.string(),
  name: z.string(),
  phoneNumber: z.string().optional(),
  email: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  isMarketingEnabled: z.boolean().default(false),
  address: z
    .object({
      id: z.string(),
      addressLine1: z.string(),
      addressLine2: z.string(),
      city: z.string(),
      state: z.string(),
      zipcode: z.string(),
      country: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
    })
    .optional(),
  organisation: z
    .object({
      id: z.string(),
      name: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
    })
    .optional(),
});

export type Venue = z.infer<typeof venueSchema>;

interface VenuesTableProps {
  data: Venue[];
  isLoading?: boolean;
  error?: string | null;
  totalCount?: number;
  pageIndex: number;
  pageSize: number;
  searchQuery: string;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSearchChange: (search: string) => void;
}

export function VenuesTable({
  data,
  isLoading = false,
  error = null,
  totalCount = 0,
  pageIndex,
  pageSize,
  searchQuery,
  onPageChange,
  onSearchChange,
}: VenuesTableProps) {
  const router = useRouter();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [editingVenue, setEditingVenue] = React.useState<Venue | null>(null);

  // Optimistic marketing toggle state: venueId → pending next value
  const [optimisticMarketing, setOptimisticMarketing] = React.useState<
    Record<string, boolean>
  >({});
  const updateVenueMutation = useUpdateVenue();

  const handleMarketingToggle = React.useCallback((venue: Venue, e: React.MouseEvent) => {
    e.stopPropagation();
    const current = optimisticMarketing[venue.id] ?? venue.isMarketingEnabled;
    const next = !current;
    setOptimisticMarketing((prev) => ({ ...prev, [venue.id]: next }));

    updateVenueMutation.mutate(
      { id: venue.id, data: { isMarketingEnabled: next } },
      {
        onSuccess: () => {
          toast.success(
            `Marketing ${next ? "enabled" : "disabled"} for ${venue.name}`,
          );
          // Don't clear the optimistic override here — the invalidated GET
          // triggered by this mutation hasn't landed yet, so nulling it now
          // would briefly flash the stale (pre-update) value before the
          // refetch confirms `next`. The effect below clears it once `data`
          // actually reflects `next`.
        },
        onError: () => {
          setOptimisticMarketing((prev) => {
            const copy = { ...prev };
            delete copy[venue.id];
            return copy;
          });
          toast.error(`Failed to update marketing for ${venue.name}`);
        },
      },
    );
  }, [optimisticMarketing, updateVenueMutation]);

  // Clear optimistic overrides once the refetched server value confirms them.
  // Clearing on mutation success/settled instead races the PATCH against the
  // invalidated GET, causing a revert-then-reapply flicker.
  React.useEffect(() => {
    setOptimisticMarketing((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      let changed = false;
      const next = { ...prev };
      for (const venue of data) {
        if (venue.id in next && next[venue.id] === venue.isMarketingEnabled) {
          delete next[venue.id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [data]);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [deletingVenue, setDeletingVenue] = React.useState<Venue | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const handleEditClick = (venue: Venue) => {
    setEditingVenue(venue);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (venue: Venue) => {
    setDeletingVenue(venue);
    setIsDeleteDialogOpen(true);
  };

  // Column definitions
  const columns: ColumnDef<Venue>[] = React.useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Venue Name",
        cell: ({ row }) => (
          <div
            className="max-w-[10rem] truncate font-medium"
            title={row.getValue("name") as string}
          >
            {row.getValue("name")}
          </div>
        ),
      },
      {
        accessorKey: "organisation",
        header: "Organisation",
        cell: ({ row }) => {
          const organisation = row.original.organisation;
          return organisation ? (
            <div className="flex min-w-0 max-w-[10rem] justify-start overflow-hidden text-left">
              <Badge
                variant="outline"
                className="min-w-0 shrink truncate capitalize text-left"
                title={organisation.name}
              >
                <span className="block min-w-0 truncate">
                  {organisation.name}
                </span>
              </Badge>
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        accessorKey: "phoneNumber",
        header: "Phone Number",
        cell: ({ row }) => {
          const phoneNumber = row.original.phoneNumber;
          return phoneNumber ? (
            <div className="text-muted-foreground">
              {formatPhoneNumber(phoneNumber)}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => {
          const email = row.original.email;
          return email ? (
            <div className="text-muted-foreground">{email}</div>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        accessorKey: "address",
        header: "Address",
        cell: ({ row }) => {
          const address = row.original.address;
          if (!address) return <span className="text-muted-foreground">—</span>;
          const fullAddress = [
            address.addressLine1,
            address.city,
            address.state,
            address.zipcode,
          ]
            .filter(Boolean)
            .join(", ");
          return (
            <div
              className="min-w-0 max-w-[10rem] truncate text-muted-foreground"
              title={fullAddress}
            >
              {fullAddress}
            </div>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        cell: ({ row }) => {
          const date = new Date(row.getValue("createdAt"));
          return (
            <div className="text-muted-foreground">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
          );
        },
      },
      {
        id: "marketing",
        header: "Marketing",
        cell: ({ row }) => {
          const venue = row.original;
          const enabled =
            optimisticMarketing[venue.id] ?? venue.isMarketingEnabled;
          const isPending = venue.id in optimisticMarketing;

          return (
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label={`Marketing ${enabled ? "enabled" : "disabled"} for ${venue.name}`}
              disabled={isPending}
              onClick={(e) => handleMarketingToggle(venue, e)}
              className={cn(
                "inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
                enabled ? "bg-primary" : "bg-input",
              )}
            >
              <span
                className={cn(
                  "pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform",
                  enabled ? "translate-x-4" : "translate-x-0",
                )}
              />
            </button>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const venue = row.original;

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="data-[state=open]:bg-muted size-8"
                  size="icon"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconDotsVertical className="size-4" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(venue);
                  }}
                >
                  <IconEdit className="mr-2 size-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(venue);
                  }}
                >
                  <IconTrash className="mr-2 size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [optimisticMarketing, handleMarketingToggle]
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(totalCount / pageSize),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
  });

  const totalPages = Math.ceil(totalCount / pageSize);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < totalPages - 1;

  return (
    <div className="w-full space-y-4">
      {/* Header with search and add button */}
      <div className="flex items-center justify-between">
        <Input
          placeholder="Search venues..."
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          className="max-w-sm"
          disabled={isLoading}
        />
        <AddVenueDialog>
          <Button>
            <IconPlus className="mr-2 size-4" />
            Add Venue
          </Button>
        </AddVenueDialog>
      </div>

      {/* Edit Venue Dialog */}
      {editingVenue && (
        <EditVenueDialog
          venue={editingVenue}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
        />
      )}

      {/* Delete Venue Dialog */}
      {deletingVenue && (
        <DeleteVenueDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          venueId={deletingVenue.id}
          venueName={deletingVenue.name}
        />
      )}

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Error loading venues: {error}
          </p>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader className="bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span className="ml-2 text-muted-foreground">
                      Loading venues...
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() =>
                    router.navigate({
                      to: `/venues/$venueId`,
                      params: { venueId: row.original.id },
                    })
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    {searchQuery
                      ? "No venues found matching your search."
                      : "No venues found. Add one to get started."}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="text-muted-foreground flex-1 text-sm">
          Showing {data.length > 0 ? pageIndex * pageSize + 1 : 0} to{" "}
          {Math.min((pageIndex + 1) * pageSize, totalCount)} of {totalCount}{" "}
          venues
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={!canPreviousPage || isLoading}
          >
            Previous
          </Button>
          <div className="text-sm font-medium">
            Page {totalPages > 0 ? pageIndex + 1 : 0} of {totalPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={!canNextPage || isLoading}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
